import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/constants";
import PropertyCard from "@/components/PropertyCard";
import LocationPicker from "@/components/LocationPicker";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Filter, SlidersHorizontal } from "lucide-react";

export default function Properties() {
  const { t, lang } = useLang();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const f = {
    type: sp.get("type") || "",
    city: sp.get("city") || "", area: sp.get("area") || "",
    price_min: sp.get("price_min") || "", price_max: sp.get("price_max") || "",
    area_min: sp.get("area_min") || "", area_max: sp.get("area_max") || "",
    bedrooms: sp.get("bedrooms") || "", q: sp.get("q") || "",
    sort: sp.get("sort") || "recent",
  };
  const update = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v) n.set(k, v); else n.delete(k); });
    setSp(n);
  };

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams(sp); p.set("limit", "24");
    api.get(`/properties?${p.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [sp]);

  const showBedrooms = ["apartment", "villa"].includes(f.type);

  const Aside = (
    <aside className="bg-white rounded-2xl border border-slate-200 p-5 h-fit space-y-4">
      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">{t.filters.type}</label>
        <Select value={f.type} onValueChange={(v) => update({ type: v === "any" ? "" : v })}>
          <SelectTrigger data-testid="f-type"><SelectValue placeholder={t.common.any} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t.common.any}</SelectItem>
            {PROPERTY_TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{lang === "ar" ? x.ar : x.en}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <LocationPicker
        city={f.city} area={f.area}
        onChange={(loc) => update(loc)}
        compact
      />

      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="f-price-min" type="number" placeholder={t.filters.priceFrom} value={f.price_min} onChange={(e) => update({ price_min: e.target.value })} />
        <Input data-testid="f-price-max" type="number" placeholder={t.filters.priceTo} value={f.price_max} onChange={(e) => update({ price_max: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="f-area-min" type="number" placeholder={t.filters.areaFrom} value={f.area_min} onChange={(e) => update({ area_min: e.target.value })} />
        <Input data-testid="f-area-max" type="number" placeholder={t.filters.areaTo} value={f.area_max} onChange={(e) => update({ area_max: e.target.value })} />
      </div>
      {showBedrooms && (
        <Input type="number" placeholder={t.filters.bedrooms} value={f.bedrooms} onChange={(e) => update({ bedrooms: e.target.value })} />
      )}
      <Input data-testid="f-q" placeholder={t.filters.search} value={f.q} onChange={(e) => update({ q: e.target.value })} />
      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">{t.filters.sort}</label>
        <Select value={f.sort} onValueChange={(v) => update({ sort: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">{t.filters.recent}</SelectItem>
            <SelectItem value="price_asc">{t.filters.priceAsc}</SelectItem>
            <SelectItem value="price_desc">{t.filters.priceDesc}</SelectItem>
            <SelectItem value="views">{t.filters.views}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" className="w-full" onClick={() => setSp(new URLSearchParams())}>{t.filters.reset}</Button>
    </aside>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="flex items-center gap-2 text-emerald-950 font-bold text-lg"><Filter className="w-4 h-4" /> {t.filters.search}</div>
        <div className="ms-auto text-sm text-slate-500">{total} {lang === "ar" ? "نتيجة" : "results"}</div>
        <Button variant="outline" size="sm" className="lg:hidden gap-1" onClick={() => setMobileOpen(!mobileOpen)}>
          <SlidersHorizontal className="w-4 h-4" /> {t.filters.filter}
        </Button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className={`${mobileOpen ? "block" : "hidden"} lg:block`}>{Aside}</div>

        <section>
          {loading ? <div className="text-center py-20 text-slate-500">{t.common.loading}</div> :
           items.length === 0 ? <div className="text-center py-20 text-slate-500 bg-white rounded-2xl border border-slate-200">{t.common.noResults}</div> :
           <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
             {items.map((p) => <PropertyCard key={p.id} property={p} listQuery={sp.toString()} />)}
           </div>
          }
        </section>
      </div>
    </div>
  );
}
